import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import get from "lodash/get";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import service from "../../services/sales";
import { rupiah } from "../../utils/currency";
import queryDefault from "../../utils/queryDefault";

export default function MonthlySales() {
  const defaultValues = {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  }
  const form = useForm({ defaultValues });

  const [options, setOptions] = useState<any>(defaultValues);
  const listSalesPerformance = useQuery(
    ["listSalesPerformance", options],
    ({ queryKey }) => {
      return service.salesPerformance({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  let thisYear = (new Date()).getFullYear();
  let allYears: number[] = [];
  for (let x = 0; x <= 20; x++) {
    allYears.push(thisYear - x);
  }

  function capitalizeFLetter(input: string) {
    return input[0].toUpperCase() + input.slice(1);
  }

  function replaceAll(str: string, find: string, replace: string) {
    return str.replace(new RegExp(find, 'g'), replace);
  }

  const tableRows = get(listSalesPerformance, "data.data", []);
  const tableColumns =
    tableRows.length > 0
      ? Object.keys(tableRows[0])
      : ["name", "omzet", "subtotal"];

  return (
    <MainLayout>
      <Breadcrumb
        label={"Sales Performance"}
        breadcrumbs={[
          { label: "Sales", href: "/app/sales" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 8, 13]}>
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1}>
            <FormControl margin="normal">
              <FormLabel>Month</FormLabel>
              <Controller
                name="month"
                control={form.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <Select
                    id="month"
                    value={form.getValues().month}
                    onChange={(event) => {
                      form.setValue("month", Number(event.target.value));
                      setOptions((prev: any) => ({
                        ...prev,
                        month: Number(event.target.value)
                      }));
                      listSalesPerformance.refetch();
                    }}
                    defaultValue={form.getValues().month}
                  >
                    <MenuItem value={0}>Select Month</MenuItem>
                    <MenuItem value={1}>January</MenuItem>
                    <MenuItem value={2}>February</MenuItem>
                    <MenuItem value={3}>March</MenuItem>
                    <MenuItem value={4}>April</MenuItem>
                    <MenuItem value={5}>May</MenuItem>
                    <MenuItem value={6}>June</MenuItem>
                    <MenuItem value={7}>July</MenuItem>
                    <MenuItem value={8}>August</MenuItem>
                    <MenuItem value={9}>September</MenuItem>
                    <MenuItem value={10}>October</MenuItem>
                    <MenuItem value={11}>November</MenuItem>
                    <MenuItem value={12}>December</MenuItem>
                  </Select>
                )}
              />
            </FormControl>

            <FormControl margin="normal">
              <FormLabel>Year</FormLabel>
              <Controller
                name="year"
                control={form.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <Select
                    id="year"
                    value={form.getValues().year}
                    onChange={(event) => {
                      form.setValue("year", Number(event.target.value));
                      setOptions((prev: any) => ({
                        ...prev,
                        year: Number(event.target.value)
                      }));
                      listSalesPerformance.refetch();
                    }}
                    defaultValue={form.getValues().year}
                  >
                    <MenuItem key={0} value={0}>
                      Select Year
                    </MenuItem>
                    {allYears.map((x) => (
                      <MenuItem key={x} value={x}>
                        {x}
                      </MenuItem>
                    )
                    )}
                  </Select>
                )}
              />
            </FormControl>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => service.downloadSalesPerformance(options)}>
            Download
          </Button>
        </Stack>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            mt: 2,
            border: "1px solid #dfe7f3",
            borderRadius: 2,
            overflowX: "auto",
            backgroundColor: "#ffffff",
          }}
        >
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow
                sx={{
                  background: "linear-gradient(90deg, #1f6fb2 0%, #12598f 100%)",
                }}
              >
                {tableColumns.map((column: string) => (
                  <TableCell
                    key={column}
                    sx={{
                      color: "#ffffff",
                      fontWeight: 700,
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {capitalizeFLetter(replaceAll(column, "_", " "))}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {listSalesPerformance.isLoading && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length} sx={{ py: 3, textAlign: "center" }}>
                    Loading, please wait
                  </TableCell>
                </TableRow>
              )}

              {!listSalesPerformance.isLoading && tableRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableColumns.length} sx={{ py: 3, textAlign: "center" }}>
                    <Typography color="text.secondary">No data available</Typography>
                  </TableCell>
                </TableRow>
              )}

              {!listSalesPerformance.isLoading &&
                tableRows.map((row: any, rowIndex: number) => (
                  <TableRow
                    key={`sales-performance-row-${rowIndex}`}
                    sx={{
                      backgroundColor: rowIndex % 2 === 0 ? "#f8fbff" : "#ffffff",
                      "&:hover": { backgroundColor: "#edf4ff" },
                    }}
                  >
                    {tableColumns.map((column: string) => (
                      <TableCell
                        key={`${rowIndex}-${column}`}
                        sx={{
                          borderBottom: "1px solid #e8eef7",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column === "omzet" || column === "subtotal"
                          ? rupiah(row[column])
                          : row[column]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
